/**
 * Nailé Creations — launch countdown strip.
 *
 * TEMPORARY: ships with sections/naile-countdown.liquid for the November 2026
 * launch and comes out again afterwards.
 *
 * Every tick recomputes the remainder from Date.now() against a fixed target
 * rather than decrementing a counter, so a cached page, a sleeping tab or a
 * throttled background timer can't put the numbers out of step.
 */

const SELECTOR = '[data-naile-countdown]';
const started = new WeakSet();

function pad(value) {
  return value < 10 ? '0' + value : String(value);
}

/**
 * Czech splits 2-4 (dny) from 5+ (dni); Romanian takes the "de" form from 20
 * upward, and at 0. Locales that don't inflect pass three identical forms, so
 * this collapses to `other` for them.
 */
function pluralize(n, forms, rule) {
  if (n === 1) return forms.one;
  if (rule === 'cs' && n >= 2 && n <= 4) return forms.few;
  if (rule === 'ro' && (n === 0 || n >= 20)) return forms.many;
  return forms.other;
}

function buildUnit(root) {
  const unit = document.createElement('span');
  unit.className = 'naile-cd__unit';

  const num = document.createElement('span');
  num.className = 'naile-cd__num';

  const label = document.createElement('span');
  label.className = 'naile-cd__u';

  unit.append(num, label);
  root.appendChild(unit);

  return { num, label };
}

function start(el) {
  if (started.has(el)) return;

  const target = Date.parse(el.dataset.target || '');
  if (Number.isNaN(target)) return;

  const slot = el.querySelector('[data-cd-time]');
  if (!slot) return;

  started.add(el);

  const d = el.dataset;
  const rule = (d.pluralRule || '').toLowerCase();
  const hideExpired = d.hideExpired !== 'false';

  const dayForms = {
    one: d.lDay || 'day',
    few: d.lDaysFew || d.lDays || 'days',
    many: d.lDaysMany || d.lDays || 'days',
    other: d.lDays || 'days',
  };
  const hourForms = {
    one: d.lHour || d.lHrs || 'hrs',
    few: d.lHoursFew || d.lHrs || 'hrs',
    many: d.lHoursMany || d.lHrs || 'hrs',
    other: d.lHrs || 'hrs',
  };
  const minuteLabel = d.lMin || 'min';
  const secondLabel = d.lSec || 'sec';

  // Replace the server-rendered date fallback with the live units.
  slot.textContent = '';
  const parts = {
    days: buildUnit(slot),
    hours: buildUnit(slot),
    minutes: buildUnit(slot),
    seconds: buildUnit(slot),
  };

  let timer = null;

  const stop = () => {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };

  const write = (part, value, label) => {
    if (part.num.textContent !== value) part.num.textContent = value;
    if (part.label.textContent !== label) part.label.textContent = label;
  };

  const tick = () => {
    // A theme-editor reload swaps in a fresh element; let the old one go.
    if (!el.isConnected) {
      stop();
      return;
    }

    const remaining = target - Date.now();

    if (remaining <= 0) {
      stop();
      if (hideExpired) {
        el.hidden = true;
      } else {
        write(parts.days, '0', pluralize(0, dayForms, rule));
        write(parts.hours, '00', pluralize(0, hourForms, rule));
        write(parts.minutes, '00', minuteLabel);
        write(parts.seconds, '00', secondLabel);
      }
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    el.hidden = false;
    write(parts.days, String(days), pluralize(days, dayForms, rule));
    write(parts.hours, pad(hours), pluralize(hours, hourForms, rule));
    write(parts.minutes, pad(minutes), minuteLabel);
    write(parts.seconds, pad(seconds), secondLabel);
  };

  tick();
  timer = setInterval(tick, 1000);

  // A backgrounded tab has its interval throttled; resync the moment it returns.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && timer !== null) tick();
  });
}

function init(root = document) {
  root.querySelectorAll?.(SELECTOR).forEach(start);
}

init();

// Theme editor: the module itself won't re-execute when a section reloads,
// so re-scan on the section lifecycle event instead.
document.addEventListener('shopify:section:load', (event) => {
  init(event.target);
});
